// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import setQuickViewProduct from "@bg/App/Handlers/ProductQuickView/SetQuickViewProduct";
import { CustomAppState } from "@bg/App/Store/Reducers";
import { getQuickViewProductState } from "@bg/App/Store/Selectors/ProductQuickView/QuickViewSelector";
import LazyImage, { LazyImageProps } from "@bg/MobiusOverrides/LazyImage";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    product: CartLineModel | ProductContextModel;
    onClick?: React.EventHandler<React.MouseEvent>;
    extendedStyles?: ProductImageStyles;
    showQuickView?: boolean;
    showSmallImage?: boolean;
}

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const productId = "product" in ownProps.product ? ownProps.product.product.id : ownProps.product.id;
    return {
        isLoading: getQuickViewProductState(state).isLoading?.hasOwnProperty(productId),
    };
};

const mapDispatchToProps = {
    setQuickViewProduct,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductImageStyles {
    wrapper?: InjectableCss;
    linkWrappingImage?: ClickablePresentationProps;
    image?: LazyImageProps;
    selectButton?: ButtonPresentationProps;
    buttonWrapper?: InjectableCss;
}

export const productImageStyles: ProductImageStyles = {
    wrapper: {
        css: css`
            position: relative;
            width: 100%;
        `,
    },
    linkWrappingImage: {
        css: css`
            width: 100%;
        `,
    },
    image: {
        css: css`
            img {
                height: 100%;
            }
        `,
    },
    buttonWrapper: {
        css: css`
            z-index: 3;
            background-color: rgba(0, 0, 0, 0.75);
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            position: absolute;
            bottom: 0;
        `,
    },
    selectButton: {
        css: css`
            &&&& {
                background: transparent;
                width: 100%;
                padding: 0;

                &:hover,
                &:focus {
                    background: ${getColor("secondary.main")};
                    box-shadow: none;
                    border: 0;
                }
            }
        `,
        typographyProps: {
            size: "15px",
            lineHeight: "18px",
            color: "common.white",
        },
    },
};

const ProductImage = ({
    product,
    onClick,
    extendedStyles,
    showSmallImage,
    setQuickViewProduct,
    isLoading,
    showQuickView = true,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(productImageStyles, extendedStyles));

    const setProductView = () => {
        setQuickViewProduct({
            productId: "product" in product ? product.product.id : product.id,
        });
    };

    let altText = "product" in product ? product.product.imageAltText : product.altText;
    if (!altText || altText === "") {
        altText = "product" in product ? product.product.productTitle : product.productName;
    }

    const imagePath =
        "product" in product
            ? showSmallImage
                ? product.product.smallImagePath || product.product.mediumImagePath
                : product.product.mediumImagePath || product.product.smallImagePath
            : product.smallImagePath;

    const productDetailPath = "product" in product ? product.productInfo.productDetailPath : product.productUri;

    return (
        <StyledWrapper {...styles.wrapper}>
            <Clickable
                {...styles.linkWrappingImage}
                href={productDetailPath}
                onClick={onClick}
                data-test-selector="productImage"
            >
                <LazyImage {...styles.image} cloudinary={true} src={imagePath} altText={altText} />
            </Clickable>
            {showQuickView && (
                <StyledWrapper className={`quick-view ${isLoading ? "loading" : ""}`} {...styles.buttonWrapper}>
                    <Button onClick={setProductView} {...styles.selectButton}>
                        {isLoading ? <LoadingSpinner size={13} color="common.white" /> : translate("Quick View")}
                    </Button>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductImage);
